import { Monitor, Barcode, Laptop, Smartphone, FlaskConical, Zap, History, Code, MonitorSmartphone, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSystemLogo } from '../hooks/useSystemLogo';

export const SelectionClientPage = () => {
  const navigate = useNavigate();
  const { logoUrl, nomeSistema } = useSystemLogo();

  const handleSelectSimulation = (type: 'web' | 'products-barcode' | 'cnes') => {
    if (type === 'web') {
      navigate('/login');
    } else if (type === 'products-barcode') {
      navigate('/products-barcode');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4 relative">

      <div className="w-full max-w-6xl">
        {/* Logo e Header */}
        <div className="text-center mb-12">
          {logoUrl && <img src={logoUrl} alt={nomeSistema} className="max-w-xs w-auto h-auto mx-auto mb-8" />}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {nomeSistema}
          </h1>
          <h2 className="text-lg font-semibold text-gray-600 mb-6">
            Gestao de Inventario
          </h2>
          <p className="text-sm text-gray-500">
            Acesse o sistema com suas credenciais
          </p>
        </div>

        {/* Cards de Selecao - Grid 3 colunas */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Card 1: Sistema Online (Nuvem) */}
          <button
            onClick={() => handleSelectSimulation('web')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-blue-700 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-blue-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-700 transition-colors duration-300">
                <Monitor className="w-10 h-10 text-blue-700 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Sistema Online (Nuvem)
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Sistema completo de gestao e relatorios
              </p>
              <div className="mt-auto pt-2">
                <span className="text-blue-700 font-semibold group-hover:text-blue-800 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </button>

          {/* Card 2: Produtos / Codigo de Barras */}
          <button
            onClick={() => handleSelectSimulation('products-barcode')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-amber-600 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-amber-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-colors duration-300">
                <Barcode className="w-10 h-10 text-amber-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Produtos / Codigo de Barras
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Visualizar produtos e gerar codigos de barras
              </p>
              <div className="mt-auto pt-2">
                <span className="text-amber-600 font-semibold group-hover:text-amber-700 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </button>

          {/* Card 3: App Mobile Oficial */}
          <a
            href="/mobile-simulator.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-emerald-600 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Zap className="w-3 h-3" />
                OFICIAL
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-emerald-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                <Smartphone className="w-10 h-10 text-emerald-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                App Mobile Oficial
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                App Flutter com dados reais do sistema
              </p>
              <div className="mt-auto pt-2">
                <span className="text-emerald-600 font-semibold group-hover:text-emerald-700 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </a>

          {/* Card 4: App Mobile Mockup */}
          <a
            href="/mobile-simulator-mockup.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-purple-600 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <FlaskConical className="w-3 h-3" />
                MOCKUP
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-purple-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-600 transition-colors duration-300">
                <Smartphone className="w-10 h-10 text-purple-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                App Mobile Mockup
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Simulador mobile com dados de teste
              </p>
              <div className="mt-auto pt-2">
                <span className="text-purple-600 font-semibold group-hover:text-purple-700 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </a>

          {/* Card 5: App Desktop Oficial */}
          <a
            href="/desktop-simulator.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-teal-600 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Zap className="w-3 h-3" />
                OFICIAL
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-teal-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors duration-300">
                <Laptop className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                App Desktop Oficial
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                App Flutter Desktop com dados reais
              </p>
              <div className="mt-auto pt-2">
                <span className="text-teal-600 font-semibold group-hover:text-teal-700 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </a>

          {/* Card 6: App Desktop Mockup */}
          <a
            href="/desktop-simulator-mockup.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-green-600 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <FlaskConical className="w-3 h-3" />
                MOCKUP
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-600 transition-colors duration-300">
                <Laptop className="w-10 h-10 text-green-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                App Desktop Mockup
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Simulador desktop com dados de teste
              </p>
              <div className="mt-auto pt-2">
                <span className="text-green-600 font-semibold group-hover:text-green-700 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </a>

          {/* Card 7: PC Windows Simulator */}
          <a
            href="/pc-windows-simulator.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-sky-600 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-sky-100 text-sky-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Zap className="w-3 h-3" />
                WINDOWS
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-sky-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-sky-600 transition-colors duration-300">
                <MonitorSmartphone className="w-10 h-10 text-sky-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                PC Windows
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Simulador Windows 11 com layout desktop
              </p>
              <div className="mt-auto pt-2">
                <span className="text-sky-600 font-semibold group-hover:text-sky-700 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </a>

          {/* Card 8: Flutter Web Dev */}
          <a
            href="/flutter-web-dev.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-orange-500 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Code className="w-3 h-3" />
                DEV
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-orange-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-500 transition-colors duration-300">
                <Code className="w-10 h-10 text-orange-500 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Flutter Web Dev
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Hot Reload - Edite e veja ao vivo
              </p>
              <div className="mt-auto pt-2">
                <span className="text-orange-500 font-semibold group-hover:text-orange-600 text-sm">
                  Acessar
                </span>
              </div>
            </div>
          </a>

          {/* Card 9: Baixar para Windows */}
          <a
            href="/download-windows.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 hover:border-indigo-600 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Download className="w-3 h-3" />
                DOWNLOAD
              </span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-indigo-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-indigo-600 transition-colors duration-300">
                <Download className="w-10 h-10 text-indigo-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Baixar para Windows
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Instalar no PC Windows 10/11
              </p>
              <div className="mt-auto pt-2">
                <span className="text-indigo-600 font-semibold group-hover:text-indigo-700 text-sm">
                  Download
                </span>
              </div>
            </div>
          </a>

        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-xs text-gray-500 mb-2">
            2025 AuditLog. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};
